import {
  Input,
  Switch,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@kognitos/lattice';
import { useTranslation } from 'react-i18next';
import { Bdkv2ConceptScalarType } from '@/generated/hooks/bdk/models';
import type { IConfigInputProps } from './types';
import { isOptionalField, unwrapInnerType } from './utils';

/**
 * ConfigInput Component
 *
 * Renders the appropriate input component based on the concept type.
 * Supports text, number, boolean, enum, and sensitive (password) input types.
 *
 * @param props - Component props
 * @returns JSX element containing the appropriate input type
 */
export function ConfigInput({
  descriptor,
  value,
  onChange,
  readOnly = false,
}: IConfigInputProps) {
  const { t } = useTranslation('automation-inputs');
  // Optional fields wrap their inner type in `optional_type` — unwrap it here so
  // an optional enum/number/boolean still renders the correct input.
  const conceptType = unwrapInnerType(descriptor);
  // Machine-readable required signal for assistive tech. The visible asterisk
  // in `ConfigFormRow` is aria-hidden (decorative); this is the actual
  // announcement screen readers pick up.
  const ariaRequired = !isOptionalField(descriptor);

  // Check for sensitive type first (password input)
  if (conceptType?.sensitive_type) {
    return (
      <Input
        type="password"
        value={String(value ?? '')}
        onChange={e => onChange(e.target.value)}
        placeholder={t('webhookDialog.configuration.placeholder')}
        className="bg-background"
        disabled={readOnly}
        aria-required={ariaRequired}
      />
    );
  }

  // Check for enum type (dropdown)
  if (conceptType?.enum_type) {
    const members = conceptType.enum_type.members ?? [];
    return (
      <Select
        value={String(value ?? '')}
        onValueChange={v => onChange(v)}
        disabled={readOnly}
      >
        <SelectTrigger
          className="w-full bg-background"
          aria-required={ariaRequired}
        >
          <SelectValue
            placeholder={t('webhookDialog.configuration.selectPlaceholder')}
          />
        </SelectTrigger>
        <SelectContent>
          {members.map(member => (
            <SelectItem key={member.name} value={member.name ?? ''}>
              {member.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  // Handle scalar types
  const scalarType = conceptType?.scalar_type;

  switch (scalarType) {
    case Bdkv2ConceptScalarType.ConceptScalarTypeBoolean:
      return (
        <Switch
          checked={Boolean(value)}
          onCheckedChange={checked => onChange(checked)}
          disabled={readOnly}
          aria-required={ariaRequired}
        />
      );

    case Bdkv2ConceptScalarType.ConceptScalarTypeNumber:
      return (
        <Input
          type="number"
          value={value !== undefined ? String(value) : ''}
          onChange={e => {
            const numValue = parseFloat(e.target.value);
            onChange(isNaN(numValue) ? 0 : numValue);
          }}
          placeholder={t('webhookDialog.configuration.placeholder')}
          className="bg-background"
          disabled={readOnly}
          aria-required={ariaRequired}
        />
      );

    case Bdkv2ConceptScalarType.ConceptScalarTypeText:
    default:
      // Default to text input for text type and unknown types
      return (
        <Input
          type="text"
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          placeholder={t('webhookDialog.configuration.placeholder')}
          className="bg-background"
          disabled={readOnly}
          aria-required={ariaRequired}
        />
      );
  }
}
